import type { ReactNode } from 'react';
import { createElement, useEffect } from 'react';
import type { ExternalToast } from 'sonner';
import { toast, useSonner } from 'sonner';

import { subscribeNotifications } from '@/api/notifications';
import { ShowAlertMoment } from '@/app/ShowAlertMoment';
import { isNullUuid, uuid } from '@/lib/uuid';
import type { RuleNotification } from '@/types/notifications';

// A rule alert is a glance, not a gallery: the whole toast stays within
// kMaxToastHeight and the snapshot is FITTED into a 16:9 preview area rather
// than sized by the image. Without this a portrait bestshot (a person, the
// common case) rendered at its own aspect ratio and stretched the toast to the
// full height of the window.
//
// kPreviewBox is sized so everything else a toast can carry — 16px of padding
// top and bottom, the title line, a two-line body and the gaps between them —
// still fits inside kMaxToastHeight. Its max-width and max-height are
// themselves 16:9, so the box holds the ratio exactly at any toast width
// instead of flattening into a letterbox strip once the height caps.
const kMaxToastHeight = 'max-h-60'; //< 15rem = 240px
const kPreviewBox = 'aspect-video w-full max-h-[144px] max-w-[256px]';

// Rule alerts are the only toasts in this corner, so the position doubles as
// the mark of an alert when counting the stack.
const kAlertPosition = 'top-right';

// Builds the toast description node: the body text (when a separate title
// exists) plus the attached snapshot, if any. `image.data` is base64
// (QByteArray::toBase64 on the media_server), so it drops into a data URI.
// Returns a string, a React node, or undefined when there is nothing to show.
// Exported for its test (and to be driveable from a page console).
export function describeNotification(event: RuleNotification): ReactNode | undefined {
    const body = event.title && event.body ? event.body : undefined;
    if (!event.image?.data) { return body; }

    // Same treatment as the event cards' PreviewFrame: a fixed 16:9 surface with
    // the frame contained inside it, so every alert is the same size whatever
    // shape the snapshot is.
    const preview = createElement(
        'div',
        {
            className: `mt-1.5 ${kPreviewBox} overflow-hidden rounded-[4px]` +
                ' bg-occ-foreground-50',
        },
        createElement('img', {
            src: `data:${event.image.mimeType || 'image/jpeg'};base64,${event.image.data}`,
            alt: '',
            className: 'size-full object-contain',
        }),
    );
    return body
        ? createElement(
            'div', null,
            // Clamped: a long body must not push the preview past the height cap.
            createElement('div', { className: 'line-clamp-2' }, body),
            preview,
        )
        : preview;
}

// Dismisses the whole alert stack. It rides in the toast's `cancel` slot, which
// sonner renders verbatim as a child of the toast itself, so it can sit against
// the toast's chrome beside the close button and travel with it as the stack
// shifts. Every alert carries one: CSS off the toast's own state leaves it to
// the front toast of an expanded stack, and a lone alert has nothing to close
// beyond itself. Exported for its test.
export function CloseAllAlerts(): ReactNode {
    // useSonner is the re-render, not the reading: its list holds only the toasts
    // published since it mounted, and the front toast — the one that shows this
    // button — mounts last of all, so it would count an empty stack forever.
    useSonner();
    // A dismissal rides the same list as the toasts themselves and carries no
    // corner, so only what has a position can be one of ours.
    const stack = toast.getToasts()
        .filter((one) => 'position' in one && one.position === kAlertPosition);
    if (stack.length < 2) { return null; }
    return createElement('button', {
        type: 'button',
        onClick: (): void => stack.forEach((one) => toast.dismiss(one.id)),
        // The close button is 20px wide and hangs a third of itself past the
        // toast's left edge; left-5 clears it and the same -35% lift shares its
        // line. The two group-data rules leave the button to the front toast of an
        // expanded stack — one selector each, since a variant pair would ask for
        // two separate ancestors. The hover tint is an image layer over the opaque
        // fill: the pill hangs past the toast's edge, and a translucent fill alone
        // would show what lies behind it.
        className: 'absolute top-0 left-5 flex h-5 -translate-y-[35%] items-center' +
            ' rounded-full border border-occ-border-2 bg-occ-background-card px-2' +
            ' text-[11px] text-occ-foreground-main hover:bg-linear-to-b' +
            ' hover:from-occ-foreground-25 hover:to-occ-foreground-25' +
            ' group-data-[front=false]:hidden group-data-[expanded=false]:hidden',
    }, 'Close all');
}

// Renders a toast whenever a `ruleNotification` frame arrives on the active
// transport's notifications stream — the cloud/local delivery of the
// `notify` rule action. Status frames ({type:'deviceStatusChanged'})
// on the same stream are ignored here; devicesStreamAtom consumes those.
//
// Mount once at the app root (alongside <Toaster/>). subscribeNotifications
// resolves its URL per-connect and stays idle until an active org exists, so
// this is safe to run before login and across org switches.
export function useRuleNotificationToasts(): void {
    useEffect(() => {
        const unsub = subscribeNotifications((event) => {
            if (event.type !== 'ruleNotification') {
                return;
            }

            const title = event.title || event.body || event.ruleName || 'Rule triggered';
            // Named up front, so that its own button can close the alert.
            const id = uuid();
            const hasMoment = !isNullUuid(event.sourceDeviceId) && event.timestampMs > 0;
            const opts: ExternalToast = {
                id,
                // Rule alerts persist until dismissed, and render top-right (a per-toast
                // override — the app's other toasts stay put).
                duration: Infinity,
                closeButton: true,
                position: kAlertPosition,
                // The height cap goes on the toast, not the toast list: no overflow
                // clipping here, because the close button hangs outside the toast's own
                // box and would be cut off with it. `group` publishes the toast's
                // expanded/front state to CloseAllAlerts.
                className: `${kMaxToastHeight} group`,
                description: describeNotification(event),
                cancel: createElement(CloseAllAlerts),
                ...(hasMoment
                    ? {
                        // Sonner renders the action verbatim as a child of the toast.
                        action: createElement(ShowAlertMoment, { alert: event, toastId: id }),
                        classNames: {
                            icon: 'pointer-events-none',
                            content: 'pointer-events-none',
                        },
                    }
                    : {}),
            };

            switch (event.severity) {
                case 'alert':
                    toast.error(title, opts);
                    break;
                case 'warning':
                    toast.warning(title, opts);
                    break;
                default:
                    toast.info(title, opts);
                    break;
            }
        });
        return unsub;
    }, []);
}
